import { CheckCircle2, Info, MapPin, Star, XCircle } from 'lucide-react'

import { READINESS_CHECKS, reasonForCheck } from 'screens/evaluations/components/approvals/ReadinessChecklist'
import { STATUS_COLORS } from 'screens/evaluations/components/EvaluationOverview'
import ProConItem, { IMPACT_COLORS } from 'screens/evaluations/components/proscons/ProConItem'
import RiskHeatGrid, { countByCell } from 'screens/evaluations/components/risks/RiskHeatGrid'
import { STATUS_CHIP } from 'screens/evaluations/components/risks/RiskProfileSummary'
import { SEVERITY_CLASSES, severityFromScore, type Severity } from 'screens/evaluations/components/risks/riskScale'
import RankingList from 'screens/evaluations/components/scoring/RankingList'
import ScoreProfileChart from 'screens/evaluations/components/scoring/ScoreProfileChart'
import WeightBar from 'screens/evaluations/components/scoring/WeightBar'
import { cn } from 'lib/utils'

import { EmptyBlock } from '../EmptyBlock'
import { asArray, asNumber, asText } from '../payload'

/**
 * Renderers for the evaluation elements (MYD-502). Each draws with the
 * evaluation screens' own components (RankingList, ScoreProfileChart,
 * WeightBar, RiskHeatGrid, ProConItem, the readiness checks), at slide size,
 * so a slide and its tab look alike and cannot drift. Payload shapes mirror
 * backend/presentations/providers/evaluationElements.js.
 *
 * Only the evaluation's owner reaches these: anyone else's blocks resolve
 * `unavailable` on the server (evaluations have no partner sharing).
 */

/** A small line under a block, for "and 3 more" and fallbacks. */
function Footnote({ children }: { children: React.ReactNode }) {
	return <p className="mt-3 text-[16px] text-gray-500">{children}</p>
}

/** A site's letter chip, as on the sites map and financial comparison. */
function Letter({ letter }: { letter: string }) {
	return <span className="mr-2 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-800 text-[14px] font-semibold text-white">{letter}</span>
}

// ---------------------------------------------------------------------------

export function EvaluationOverviewBlock({ payload }: { payload: any }) {
	const status = asText(payload?.status)
	const selected = payload?.selectedSite
	const counts = payload?.counts ?? {}
	const tiles: Array<[string, unknown]> = [
		['Sites', counts.sites],
		['Criteria', counts.criteria],
		['Risks', counts.risks],
		['Pros & cons', counts.prosCons],
	]

	return (
		<div className="space-y-6">
			<div className="flex flex-wrap items-center gap-3 text-[18px] text-gray-600">
				{status && <span className={cn('rounded-full px-3 py-1 text-[16px] font-medium', STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700')}>{asText(payload?.statusLabel)}</span>}
				{asText(payload?.marketArea) && (
					<span className="inline-flex items-center gap-1.5">
						<MapPin className="h-5 w-5 text-gray-400" /> {asText(payload.marketArea)}
					</span>
				)}
				{asText(payload?.portfolioName) && <span>· {asText(payload.portfolioName)}</span>}
			</div>
			{asText(payload?.description) && <p className="text-[20px] leading-snug text-gray-700">{asText(payload.description)}</p>}
			{selected && (
				<div className="flex items-center gap-3 rounded-xl border-2 border-[#0080fe] bg-[#0080fe]/5 px-6 py-4">
					<Star className="h-6 w-6 text-[#0080fe]" />
					<span className="text-[18px] uppercase tracking-wide text-gray-500">Selected site</span>
					<Letter letter={asText(selected.letter)} />
					<span className="text-[24px] font-semibold text-gray-900">{asText(selected.name)}</span>
				</div>
			)}
			<div className="grid grid-cols-4 gap-4">
				{tiles.map(([label, value]) => (
					<div key={label} className="rounded-xl bg-gray-50 px-5 py-4">
						<div className="text-[16px] uppercase tracking-wide text-gray-500">{label}</div>
						<div className="text-[36px] font-bold text-gray-900">{asNumber(value) ?? 0}</div>
					</div>
				))}
			</div>
		</div>
	)
}

// ---------------------------------------------------------------------------

export function EvaluationRankingBlock({ payload }: { payload: any }) {
	const entries = asArray<any>(payload?.entries)
	const more = asNumber(payload?.more) ?? 0
	const unscored = asNumber(payload?.unscored) ?? 0
	if (entries.length === 0) return <EmptyBlock>No site has been scored yet.</EmptyBlock>

	return (
		<div>
			<RankingList
				size="slide"
				entries={entries.map(entry => ({
					id: `${asText(entry?.letter)}-${asNumber(entry?.rank)}`,
					name: asText(entry?.name),
					score: Number.parseFloat(asText(entry?.score)) || null,
					badge: entry?.selected ? 'Selected' : undefined,
				}))}
			/>
			{(more > 0 || unscored > 0) && (
				<Footnote>
					{[more > 0 && `and ${more} more`, unscored > 0 && `${unscored} not yet scored`].filter(Boolean).join(' · ')}
				</Footnote>
			)}
		</div>
	)
}

export function EvaluationScoreProfileBlock({ payload }: { payload: any }) {
	const criteria = asArray<string>(payload?.criteria).map(asText)
	const sites = asArray<any>(payload?.sites).map(site => ({
		name: `${asText(site?.letter)} · ${asText(site?.name)}`,
		values: asArray<number | null>(site?.values).map(value => asNumber(value)),
	}))
	if (criteria.length < 3 || sites.length === 0) {
		return <EmptyBlock fill>{sites.length === 0 ? 'No site has been scored yet.' : 'A radar needs at least three criteria.'}</EmptyBlock>
	}
	return (
		<div className="h-full min-h-[320px]">
			<ScoreProfileChart size="slide" criteria={criteria} sites={sites} />
		</div>
	)
}

export function EvaluationWeightsBlock({ payload }: { payload: any }) {
	const criteria = asArray<any>(payload?.criteria)
	const totalWeight = asNumber(payload?.totalWeight) ?? 0
	if (criteria.length === 0) return <EmptyBlock>No scoring criteria yet.</EmptyBlock>

	return (
		<div className="space-y-4">
			{criteria.map((criterion, index) => {
				const weight = asNumber(criterion?.weight) ?? 0
				return (
					<div key={index}>
						<div className="mb-1.5 flex items-baseline justify-between gap-4">
							<span className="truncate text-[20px] font-medium text-gray-900">
								{asText(criterion?.name)}
								{asText(criterion?.category) && <span className="ml-2 text-[15px] font-normal text-gray-500">{asText(criterion.category)}</span>}
							</span>
							<span className="shrink-0 text-[20px] font-semibold tabular-nums text-gray-900">{weight}%</span>
						</div>
						<WeightBar weight={weight} totalWeight={totalWeight} className="h-3" />
					</div>
				)
			})}
		</div>
	)
}

export function EvaluationComparisonMatrixBlock({ payload }: { payload: any }) {
	const sites = asArray<any>(payload?.sites)
	const rows = asArray<any>(payload?.rows)
	if (sites.length === 0 || rows.length === 0) return <EmptyBlock>No site has been scored yet.</EmptyBlock>

	return (
		<table className="w-full table-fixed text-left">
			<thead>
				<tr className="border-b-2 border-gray-200 text-[16px] text-gray-500">
					<th className="w-[30%] py-2 pr-4 font-semibold uppercase tracking-wide">Criterion</th>
					{sites.map((site, index) => (
						<th key={index} className="py-2 pl-3 text-right font-semibold text-gray-800">
							<span className="inline-flex items-center justify-end">
								<Letter letter={asText(site?.letter)} />
								<span className="truncate">{asText(site?.name)}</span>
							</span>
						</th>
					))}
				</tr>
			</thead>
			<tbody>
				{rows.map((row, index) => {
					const best = asArray<boolean>(row?.best)
					return (
						<tr key={index} className="border-b border-gray-100 text-[19px]">
							<td className="truncate py-2 pr-4 text-gray-700">
								{asText(row?.criterion)} <span className="text-[14px] text-gray-400">{asNumber(row?.weight) ?? 0}%</span>
							</td>
							{asArray<string | null>(row?.values).map((value, cell) => (
								<td key={cell} className={cn('py-2 pl-3 text-right tabular-nums', best[cell] ? 'font-semibold text-success' : 'text-gray-800')}>
									{asText(value) || '—'}
								</td>
							))}
						</tr>
					)
				})}
			</tbody>
			<tfoot>
				<tr className="bg-blue-50/40 text-[20px] font-semibold text-gray-900">
					<td className="py-2.5 pr-4">Weighted score</td>
					{sites.map((site, index) => (
						<td key={index} className="py-2.5 pl-3 text-right tabular-nums">
							{asText(site?.overall) || '—'}
						</td>
					))}
				</tr>
			</tfoot>
		</table>
	)
}

// ---------------------------------------------------------------------------

export function EvaluationRiskHeatMapBlock({ payload }: { payload: any }) {
	const risks = asArray<any>(payload?.risks)
	const totals: Record<Severity, number> = { low: 0, medium: 0, high: 0 }
	for (const risk of risks) totals[severityFromScore((asNumber(risk?.likelihood) ?? 0) * (asNumber(risk?.impact) ?? 0))] += 1

	return (
		<div>
			<div className="mb-3 flex items-center justify-between gap-3">
				<span className="text-[17px] text-gray-500">
					{asText(payload?.scopeLabel)} · {risks.length} {risks.length === 1 ? 'risk' : 'risks'}
				</span>
				<span className="flex gap-2">
					{(['low', 'medium', 'high'] as Severity[]).map(severity => (
						<span key={severity} className={cn('rounded-full px-3 py-0.5 text-[15px] font-medium', SEVERITY_CLASSES[severity].chip)}>
							{SEVERITY_CLASSES[severity].label} {totals[severity]}
						</span>
					))}
				</span>
			</div>
			<RiskHeatGrid size="slide" counts={countByCell(risks)} />
			{asText(payload?.note) && <Footnote>{asText(payload.note)}</Footnote>}
		</div>
	)
}

export function EvaluationTopRisksBlock({ payload }: { payload: any }) {
	const risks = asArray<any>(payload?.risks)
	const more = asNumber(payload?.more) ?? 0
	if (risks.length === 0) return <EmptyBlock>{asText(payload?.note) || 'No open risks recorded.'}</EmptyBlock>

	return (
		<div>
			<ul className="space-y-3">
				{risks.map((risk, index) => {
					const severity = (['low', 'medium', 'high'].includes(risk?.severity) ? risk.severity : 'low') as Severity
					return (
						<li key={index} className="flex items-center gap-4 rounded-xl border border-gray-100 bg-white px-5 py-3">
							<span className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[18px] font-bold', SEVERITY_CLASSES[severity].chip)}>
								{asNumber(risk?.score) ?? ''}
							</span>
							<div className="min-w-0 flex-1">
								<p className="truncate text-[21px] font-medium text-gray-900">{asText(risk?.name)}</p>
								<p className="flex items-center text-[16px] text-gray-500">
									<Letter letter={asText(risk?.site?.letter)} />
									{asText(risk?.site?.name)}
									{asText(risk?.category) && ` · ${asText(risk.category)}`}
								</p>
							</div>
							<span className={cn('shrink-0 rounded-full px-3 py-1 text-[14px] font-semibold uppercase', STATUS_CHIP[asText(risk?.status)] ?? 'bg-gray-100 text-gray-700')}>
								{asText(risk?.statusLabel)}
							</span>
						</li>
					)
				})}
			</ul>
			{(more > 0 || asText(payload?.note)) && <Footnote>{[more > 0 && `and ${more} more`, asText(payload?.note)].filter(Boolean).join(' · ')}</Footnote>}
		</div>
	)
}

// ---------------------------------------------------------------------------

/**
 * One site's pros or cons. A single site gets the Pros/Cons tab's own item
 * cards; two or three sites share the slide, where full cards overflow it, so
 * each item is one line with the same impact pill.
 */
function ProConColumn({ title, items, more, tone, compact }: { title: string; items: any[]; more: number; tone: 'pro' | 'con'; compact: boolean }) {
	const Icon = tone === 'pro' ? CheckCircle2 : XCircle
	return (
		<div className="min-w-0 flex-1">
			<h5 className={cn('mb-2 flex items-center gap-1.5 text-[16px] font-semibold uppercase tracking-wide', tone === 'pro' ? 'text-green-700' : 'text-red-700')}>
				<Icon className="h-5 w-5" /> {title}
			</h5>
			{items.length === 0 ? (
				<p className="text-[17px] text-gray-400">None recorded.</p>
			) : compact ? (
				<ul className="space-y-1.5">
					{items.map((item, index) => (
						<li key={index} className="flex items-center gap-2 text-[18px] text-gray-800">
							{asText(item?.impactLevel) && (
								<span className={cn('shrink-0 rounded border px-1.5 text-[12px] font-medium', IMPACT_COLORS[asText(item.impactLevel)] || IMPACT_COLORS.LOW)}>
									{asText(item.impactLevel)}
								</span>
							)}
							<span className="truncate">{asText(item?.description)}</span>
						</li>
					))}
				</ul>
			) : (
				<div className="space-y-2">
					{items.map((item, index) => (
						<ProConItem key={index} item={item} size="slide" />
					))}
				</div>
			)}
			{more > 0 && <p className={cn('text-[15px] text-gray-500', compact ? 'mt-1' : 'mt-2')}>and {more} more</p>}
		</div>
	)
}

export function EvaluationProsConsBlock({ payload }: { payload: any }) {
	const sites = asArray<any>(payload?.sites)
	if (sites.length === 0) return <EmptyBlock>This evaluation has no sites yet.</EmptyBlock>
	const single = sites.length === 1

	return (
		<div>
			<div className={cn('flex gap-6', !single && 'divide-x divide-gray-200')}>
				{sites.map((site, index) => (
					<div key={index} className={cn('min-w-0 flex-1', !single && index > 0 && 'pl-6')}>
						{!single && (
							<h4 className="mb-3 flex items-center text-[20px] font-semibold text-gray-900">
								<Letter letter={asText(site?.letter)} /> {asText(site?.name)}
							</h4>
						)}
						<div className={cn(single ? 'flex gap-6' : 'space-y-5')}>
							<ProConColumn title="Pros" tone="pro" items={asArray(site?.pros)} more={asNumber(site?.morePros) ?? 0} compact={!single} />
							<ProConColumn title="Cons" tone="con" items={asArray(site?.cons)} more={asNumber(site?.moreCons) ?? 0} compact={!single} />
						</div>
					</div>
				))}
			</div>
			{asText(payload?.note) && <Footnote>{asText(payload.note)}</Footnote>}
		</div>
	)
}

// ---------------------------------------------------------------------------

/** The Approvals tab's checklist, from the same checks and reason strings. */
export function EvaluationReadinessBlock({ payload }: { payload: any }) {
	const snapshot = { ready: Boolean(payload?.ready), reasons: asArray<string>(payload?.reasons).map(asText) }

	return (
		<div>
			<ul className="space-y-2">
				{READINESS_CHECKS.map(check => {
					const passes = check.pass(snapshot)
					const isInfo = check.informational
					const reason = reasonForCheck(snapshot, check)
					const Icon = isInfo ? Info : passes ? CheckCircle2 : XCircle
					return (
						<li key={check.id} className="flex items-center gap-4 px-2 py-2">
							<Icon className={cn('h-8 w-8 shrink-0', isInfo ? 'text-gray-400' : passes ? 'text-emerald-500' : 'text-red-500')} />
							<div className="min-w-0 flex-1">
								<p className={cn('text-[21px] font-medium', isInfo ? 'text-gray-700' : passes ? 'text-gray-900' : 'text-red-700')}>{check.label}</p>
								{!passes && reason && <p className="text-[16px] text-red-600">{reason}</p>}
							</div>
						</li>
					)
				})}
			</ul>
			<div className={cn('mt-4 rounded-xl px-5 py-3 text-[19px] font-medium', snapshot.ready ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800')}>
				{snapshot.ready ? 'Ready to submit for approval.' : 'Not ready for approval yet.'}
				{asText(payload?.statusLabel) && <span className="ml-2 font-normal opacity-80">Status: {asText(payload.statusLabel)}</span>}
			</div>
		</div>
	)
}
